// The merge. There isn't one: union by event id, sort, fold.
//
// Pure and total. It is handed rows written by other builds and by other
// devices, so "total" here means total over hostile input, not merely over
// well-formed input.

import type { GameState } from "../state.ts";
import { compareStamps, type DeviceId, type EventId, type Stamp } from "./clock.ts";
import type { ProgressEvent } from "./event.ts";
import { applyEvent } from "./reduce.ts";

/**
 * The total order events fold in.
 *
 * Three terms, and all three are needed. The stamp orders across devices; the
 * device id breaks a stamp tie the same way on every device; the event id
 * breaks the remaining tie, which only a clock bug or a duplicated device id
 * can produce. Ordering by arrival, by `$createdAt`, or by wall time would
 * each give a different answer on different devices.
 */
export const order = (e: ProgressEvent): readonly [Stamp, DeviceId, EventId] => [e.at, e.dev, e.id];

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

function byOrder(a: ProgressEvent, b: ProgressEvent): number {
	const [as, ad, ai] = order(a);
	const [bs, bd, bi] = order(b);
	return compareStamps(as, bs) || cmp(ad, bd) || cmp(ai, bi);
}

/**
 * Fold a set of events onto a base state.
 *
 * `base` is a snapshot — local, remote, or `defaultState()`. Every
 * snapshot is a cache, so calling this with `defaultState()` and the whole
 * history is always correct and is what to do whenever anything is in doubt.
 *
 * Returns `base` itself, not a copy, when there is nothing to fold: callers
 * compare identity to decide whether to persist.
 */
export function fold(base: GameState, events: readonly ProgressEvent[]): GameState {
	if (!events.length) return base;
	const unique = new Map<EventId, ProgressEvent>();
	// First write wins on a duplicate id. The two copies are the same event by
	// construction — the id is the Appwrite row id — so this only decides which
	// object identity survives, never which facts do.
	for (const e of events) if (!unique.has(e.id)) unique.set(e.id, e);
	return [...unique.values()].sort(byOrder).reduce(applyEvent, base);
}
